import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Webinar } from '@proref/types';
import { useWebinarsQuery } from '../../hooks/use-webinars';
import { creditLine } from '../../lib/webinar-platform';
import { WebinarDetailModal } from '../webinars/WebinarDetailModal';
import { Skeleton } from '../shared/Skeleton';
import { Icon } from '../shared/Icons';

// The next few webinars the referee can attend — live from GET /v1/webinars (their federation's plus
// platform-wide general ones, already upcoming + soonest-first). The SOONEST is promoted to a
// highlighted "Next up" hero with a one-click reserve; the rest render as compact rows. Full detail +
// cancelling live on the Webinars page, linked from the header.
const PREVIEW_LIMIT = 3;

// Weekday + day-of-month for the date chip (WED / 29). Local time — the referee plans around the
// webinar's wall-clock start.
function chipParts(iso: string): { weekday: string; day: string } {
  const d = new Date(iso);
  return {
    weekday: d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
    day: d.toLocaleDateString('en-US', { day: 'numeric' }),
  };
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

// "19:00 · Boris Milosevic · 18 joining" — the meta line shared by the hero and the rows. The name
// is the SPEAKERS when the webinar lists any, falling back to the hosting admin: a referee scanning
// this card cares who is presenting, which is rarely the admin who created the entry.
//
// A plain function, not a hook, so it takes `t` as a parameter rather than calling useTranslation()
// itself (which only components/hooks may do) — both call sites already have it from their own render.
function metaLine(w: Webinar, t: TFunction): string {
  return [timeLabel(w.scheduled_at), creditLine(w), t('home.webinars.joining', { count: w.reservation_count })]
    .filter(Boolean)
    .join(' · ');
}

export function UpcomingWebinarsCard() {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useWebinarsQuery();
  const webinars = (data ?? []).slice(0, PREVIEW_LIMIT);
  const [next, ...rest] = webinars;

  // The webinar whose reserve modal is open (null = closed). Kept as the object so the modal has the
  // live reservation state without another lookup.
  const [reserving, setReserving] = useState<Webinar | null>(null);

  return (
    <section className="rounded border border-line bg-surface p-5 shadow-1">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold text-ink">{t('home.webinars.title')}</h2>
          <p className="mt-0.5 text-xs text-muted">{t('home.webinars.subtitle')}</p>
        </div>
        <Link
          to="/webinars"
          className="inline-flex flex-none items-center gap-1 text-xs font-medium text-accent hover:text-accent-2"
        >
          {t('home.webinars.viewAll')}
          <Icon.arrow className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <div className="space-y-2.5">
            <Skeleton className="h-[104px] w-full" />
            <Skeleton className="h-[58px] w-full" />
          </div>
        ) : isError && !data ? (
          // Fetch failures render inline (per conventions), not as a toast.
          <div className="rounded border border-dashed border-danger bg-danger-tint px-4 py-6 text-center">
            <p className="text-xs text-danger">{t('home.webinars.couldNotLoad')}</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-1.5 text-[11px] font-medium text-ink underline underline-offset-2 hover:text-ink-2"
            >
              {t('common.tryAgain')}
            </button>
          </div>
        ) : webinars.length === 0 ? (
          <div className="rounded border border-dashed border-line-2 bg-bg-2 px-4 py-8 text-center">
            <p className="text-xs font-medium text-ink">{t('home.webinars.none')}</p>
            <p className="mt-1 text-[11px] text-muted">{t('home.webinars.checkBack')}</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {next && <NextUpHero webinar={next} onReserve={() => setReserving(next)} />}
            {rest.map((w) => (
              <WebinarRow key={w.id} webinar={w} onOpen={() => setReserving(w)} />
            ))}
          </div>
        )}
      </div>

      <WebinarDetailModal webinar={reserving} open={reserving !== null} onClose={() => setReserving(null)} />
    </section>
  );
}

// The soonest webinar, promoted to a navy hero with a one-click reserve — the card's focal point.
function NextUpHero({ webinar, onReserve }: { webinar: Webinar; onReserve: () => void }) {
  const { t } = useTranslation();
  const isFull = webinar.seats_left === 0 && !webinar.is_reserved;

  return (
    // Navy hero (the Federation palette's `accent` token = navy, white foreground) — the card's focal
    // point, deliberately not `ink` (which is near-black).
    <div className="rounded-sm bg-accent p-4 text-accent-ink shadow-1">
      <div className="flex items-center gap-2">
        <span className="rounded-sm bg-white/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
          {t('home.webinars.nextUp')}
        </span>
        <span className="min-w-0 truncate text-[11px] text-accent-ink/70">{metaLine(webinar, t)}</span>
      </div>

      <div className="mt-2 flex items-end justify-between gap-3">
        <h3 className="min-w-0 font-display text-[16px] leading-snug text-accent-ink">{webinar.title}</h3>

        {webinar.is_reserved ? (
          // Already reserved — a static confirmation, not a button (cancelling lives on the page).
          <span className="inline-flex flex-none items-center gap-1 rounded-btn bg-white/15 px-3 py-1.5 text-xs font-semibold text-accent-ink">
            <Icon.check className="h-3.5 w-3.5" /> {t('home.webinars.reserved')}
          </span>
        ) : (
          <button
            type="button"
            onClick={onReserve}
            disabled={isFull}
            className="inline-flex flex-none items-center gap-1 rounded-btn bg-surface px-3 py-1.5 text-xs font-semibold text-accent transition-colors hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isFull ? t('home.webinars.full') : t('home.webinars.reserveSeat')}
            {!isFull && <Icon.arrow className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}

// A compact row for the webinars after the hero — date chip, title, meta, and a Live/Group tag. The
// whole row opens the reserve modal.
function WebinarRow({ webinar, onOpen }: { webinar: Webinar; onOpen: () => void }) {
  const { t } = useTranslation();
  const { weekday, day } = chipParts(webinar.scheduled_at);
  const isLive = webinar.type === 'live';

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-3 rounded-sm border border-line-2 bg-bg-2 p-2.5 text-left transition-colors hover:border-line-3"
    >
      {/* Date chip. */}
      <div className="flex h-11 w-11 flex-none flex-col items-center justify-center rounded-sm bg-surface shadow-1">
        <span className="text-[9px] font-semibold uppercase tracking-wide text-muted">{weekday}</span>
        <span className="font-display text-lg leading-none text-ink-3">{day}</span>
      </div>

      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-medium text-ink">{webinar.title}</div>
        <div className="mt-0.5 truncate text-[11px] text-muted">{metaLine(webinar, t)}</div>
      </div>

      {/* Live vs Group — Live gets the accent pill, Group a neutral one. */}
      <span
        className={`flex-none rounded-sm px-2 py-0.5 text-[10px] font-semibold ${
          isLive ? 'bg-accent-tint text-accent' : 'bg-line-2 text-muted'
        }`}
      >
        {isLive ? t('home.webinars.live') : t('home.webinars.group')}
      </span>
    </button>
  );
}
